import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import { Table, Button, Modal, Form, Row, Col, Alert } from "react-bootstrap";

export default class HotelRoom extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      rooms: [],
      roomTypes: [],
      roomStatuses: [],
      showModal: false,
      editItem: null,
      error: null,
      loading: false,
      saving: false,
      newItem: {
        storeId: "",
        roomTypeId: "",
        roomNumber: "",
        floor: "",
        statusId: "",
        isActive: true,
      },
    };
  }

  componentDidMount() {
    this.loadAllData();
  }

  // central helper to build API url with storeId query
  apiUrl(resource) {
    const storeId = this.context?.storeId;
    const base = `${process.env.REACT_APP_API}${resource}`;
    return storeId ? `${base}?storeId=${storeId}` : base;
  }

  async loadAllData() {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      // fetch lists with storeId query param
      const [roomRes, typeRes, statusRes] = await Promise.all([
        fetch(this.apiUrl("HotelRoom")),
        fetch(this.apiUrl("HotelRoomType")),
        fetch(this.apiUrl("HotelRoomStatus")),
      ]);

      if (!roomRes.ok || !typeRes.ok || !statusRes.ok) {
        // try to extract any server-side message
        const texts = await Promise.all([
          roomRes.ok ? Promise.resolve(null) : roomRes.text().catch(() => null),
          typeRes.ok ? Promise.resolve(null) : typeRes.text().catch(() => null),
          statusRes.ok
            ? Promise.resolve(null)
            : statusRes.text().catch(() => null),
        ]);
        const msg =
          texts.filter(Boolean).join(" | ") || "Failed to load some resources";
        throw new Error(msg);
      }

      const [rooms, roomTypes, roomStatuses] = await Promise.all([
        roomRes.json(),
        typeRes.json(),
        statusRes.json(),
      ]);

      this.setState({
        rooms,
        roomTypes,
        roomStatuses,
      });
    } catch (err) {
      console.error("Error loading room data:", err);
      this.setState({ error: err.message || "Error loading room data." });
    } finally {
      this.setState({ loading: false });
    }
  }

  handleShowModal = (item = null) => {
    const storeId = this.context?.storeId || "";
    if (item) {
      // ensure editing model maps fields properly (IDs, not nested objects)
      this.setState({
        showModal: true,
        editItem: item,
        newItem: {
          storeId,
          roomTypeId:
            item.roomTypeId ||
            (item.roomType && item.roomType.roomTypeId) ||
            "",
          roomNumber: item.roomNumber || "",
          floor: item.floor || "",
          statusId:
            item.statusId || (item.status && item.status.roomStatusId) || "",
          isActive: item.isActive ?? true,
        },
        error: null,
      });
    } else {
      this.setState({
        showModal: true,
        editItem: null,
        newItem: {
          storeId,
          roomTypeId: "",
          roomNumber: "",
          floor: "",
          statusId: "",
          isActive: true,
        },
        error: null,
      });
    }
  };

  handleCloseModal = () =>
    this.setState({ showModal: false, editItem: null, error: null });

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prev) => ({
      newItem: {
        ...prev.newItem,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  async handleSave() {
    const { newItem, editItem } = this.state;
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }

    // basic validation
    if (!newItem.roomNumber) {
      this.setState({ error: "Room Number is required." });
      return;
    }
    if (!newItem.roomTypeId) {
      this.setState({ error: "Room Type is required." });
      return;
    }

    // prepare payload and url (append storeId query)
    const apiBase = `${process.env.REACT_APP_API}HotelRoom`;
    const url = editItem
      ? `${apiBase}/${editItem.roomId}?storeId=${storeId}`
      : `${apiBase}?storeId=${storeId}`;

    const payload = {
      storeId,
      roomTypeId: newItem.roomTypeId,
      roomNumber: newItem.roomNumber,
      floor: newItem.floor,
      statusId: newItem.statusId || null,
      isActive: !!newItem.isActive,
    };

    this.setState({ saving: true, error: null });

    try {
      const res = await fetch(url, {
        method: editItem ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let txt;
        try {
          txt = await res.text();
        } catch {
          txt = res.statusText;
        }
        throw new Error(`Save failed: ${res.status} ${txt}`);
      }

      this.handleCloseModal();
      await this.loadAllData();
    } catch (err) {
      console.error("Error saving room:", err);
      this.setState({ error: err.message || "Failed to save room." });
    } finally {
      this.setState({ saving: false });
    }
  }

  async handleDelete(id) {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }
    if (!window.confirm("Are you sure you want to delete this room?")) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}HotelRoom/${id}?storeId=${storeId}`,
        { method: "DELETE" }
      );

      if (!res.ok) {
        let txt;
        try {
          txt = await res.text();
        } catch {
          txt = res.statusText;
        }
        throw new Error(`Delete failed: ${res.status} ${txt}`);
      }

      await this.loadAllData();
    } catch (err) {
      console.error("Error deleting room:", err);
      this.setState({ error: err.message || "Failed to delete room." });
    }
  }

  render() {
    const {
      rooms,
      roomTypes,
      roomStatuses,
      showModal,
      newItem,
      editItem,
      error,
      loading,
      saving,
    } = this.state;

    return (
      <div className="container mt-4">
        <h4>Hotel Rooms</h4>

        {error && <Alert variant="danger">{error}</Alert>}

        <Button
          variant="primary"
          className="mb-3"
          onClick={() => this.handleShowModal()}
        >
          Add Room
        </Button>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <Table bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th>Room No</th>
                <th>Floor</th>
                <th>Type</th>
                <th>Status</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((r) => (
                <tr key={r.roomId}>
                  <td>{r.roomNumber}</td>
                  <td>{r.floor}</td>
                  <td>{r.roomType?.name ?? r.roomTypeName ?? "—"}</td>
                  <td>{r.status?.code ?? r.statusCode ?? "—"}</td>
                  <td>{r.isActive ? "Yes" : "No"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => this.handleShowModal(r)}
                    >
                      Edit
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.handleDelete(r.roomId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Modal show={showModal} onHide={this.handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editItem ? "Edit Room" : "Add Room"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Room Number</Form.Label>
                    <Form.Control
                      name="roomNumber"
                      value={newItem.roomNumber}
                      onChange={this.handleChange}
                      placeholder="E.g., 101"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Floor</Form.Label>
                    <Form.Control
                      name="floor"
                      value={newItem.floor}
                      onChange={this.handleChange}
                      placeholder="E.g., 1st Floor"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Room Type</Form.Label>
                <Form.Select
                  name="roomTypeId"
                  value={newItem.roomTypeId}
                  onChange={this.handleChange}
                >
                  <option value="">-- Select Type --</option>
                  {roomTypes.map((t) => (
                    <option key={t.roomTypeId} value={t.roomTypeId}>
                      {t.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="statusId"
                  value={newItem.statusId}
                  onChange={this.handleChange}
                >
                  <option value="">-- Select Status --</option>
                  {roomStatuses.map((s) => (
                    <option key={s.roomStatusId} value={s.roomStatusId}>
                      {s.code}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formIsActive">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  name="isActive"
                  checked={!!newItem.isActive}
                  onChange={this.handleChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => this.handleSave()}
              disabled={saving}
            >
              {saving ? "Saving..." : editItem ? "Update" : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
